import {AsideForm} from '@/components/AsideForm';
import {LabeledGridItem} from '@/components/LabeledGridItem';
import {Toolbar} from '@/components/Toolbar';
import {dogStatusChoices, sexChoices, stateChoices} from '@/lib/choices';
import {formatUserDisplayName} from '@/utils/formatUserDisplayName';
import {DogBreedData} from '@/types';
import {
    alpha,
    Box,
    Button,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type {Theme} from '@mui/material/styles';
import {styled} from '@mui/material/styles';
import {
    AutocompleteInput,
    BooleanField,
    BooleanInput,
    DateInput,
    Edit,
    FunctionField,
    ImageField,
    ImageFieldClasses,
    NumberInput,
    ReferenceField,
    SaveButton,
    SelectInput,
    TabbedForm,
    TextField,
    TextInput,
    required,
    useEditContext,
    useGetList,
    useGetOne,
    useRecordContext,
} from 'react-admin';
import {useMemo} from 'react';
import {useWatch} from 'react-hook-form';
import {Link as RouterLink} from 'react-router-dom';

const shellSx = (theme: Theme) => ({
    borderRadius: 0,
    border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
});

const ProfileImageField = styled(ImageField)({
    width: '100%',
    margin: 0,
    [`& .${ImageFieldClasses.image}`]: {
        margin: 0,
        width: '100%',
        height: 200,
        objectFit: 'contain',
        objectPosition: 'left',
    },
});

const GalleryImageField = styled(ImageField)({
    [`& .${ImageFieldClasses.list}`]: {
        padding: 0,
        gap: 10,
    },
    [`& .${ImageFieldClasses.list} li`]: {
        display: 'flex',
        position: 'relative',
    },
    [`& .${ImageFieldClasses.list} li:nth-of-type(n+4)`]: {
        display: 'none',
    },
    [`& .${ImageFieldClasses.list} li:nth-of-type(3)::after`]: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        backgroundColor: '#3339',
    },
    [`& .${ImageFieldClasses.image}`]: {
        margin: 0,
        width: 100,
        height: 100,
        objectFit: 'cover',
    },
});

const Title = () => {
    const {record} = useEditContext();
    if (record && 'name' in record) {
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    minWidth: 0,
                    maxWidth: '100%',
                    flexWrap: 'wrap',
                    columnGap: 0.5,
                    rowGap: 0.25,
                }}
            >
                <Typography component="span" variant="inherit" noWrap>
                    Dogs / {record.name}
                </Typography>
                {record.user_id ? (
                    <>
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.secondary"
                            sx={{flexShrink: 0}}
                        >
                            /
                        </Typography>
                        <ReferenceField
                            source="user_id"
                            reference="users"
                            link="show"
                            sx={{
                                display: 'inline',
                                minWidth: 0,
                                maxWidth: '100%',
                                '& a': {
                                    color: 'inherit',
                                    fontWeight: 600,
                                    textUnderlineOffset: 3,
                                },
                            }}
                        >
                            <FunctionField
                                render={(user) => (
                                    <Typography
                                        component="span"
                                        variant="inherit"
                                        noWrap
                                    >
                                        {formatUserDisplayName(user)}
                                    </Typography>
                                )}
                            />
                        </ReferenceField>
                    </>
                ) : null}
            </Box>
        );
    }
    return <>Dogs</>;
};

const GalleryImages = () => {
    const images = useWatch({name: 'images'});

    if (!images) {
        return null;
    }

    const overThreeAmount = images.length - 3;

    return (
        <GalleryImageField
            source="images"
            label="Gallery images"
            src="url"
            sx={
                overThreeAmount > 0
                    ? {
                          [`& .${ImageFieldClasses.list} li:nth-child(3)::after`]:
                              {
                                  content: `'+${overThreeAmount}'`,
                              },
                      }
                    : undefined
            }
        />
    );
};

type OwnerLicenseRow = {
    id: number;
    organisation?: string;
    code?: string | number | null;
    status?: string;
    expiry?: string | null;
};

const OwnerLicensesReadOnly = () => {
    const record = useRecordContext<{user_id?: string}>();
    const userId = record?.user_id;
    const {data: owner, isLoading, error} = useGetOne(
        'users',
        {id: userId as string},
        {enabled: Boolean(userId)}
    );

    const licenses: OwnerLicenseRow[] = Array.isArray(owner?.licenses)
        ? owner.licenses
        : [];

    return (
        <Box sx={(theme) => ({...shellSx(theme), mt: 2.5})}>
            <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600}}
                >
                    Owner licences
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                    From the owner&apos;s account (single source). Open a record to
                    edit.
                </Typography>
            </Box>
            <Divider />
            {isLoading ? (
                <Box sx={{px: 2.5, py: 2}}>
                    <Typography variant="body2" color="text.secondary">
                        Loading licences…
                    </Typography>
                </Box>
            ) : null}
            {error ? (
                <Box sx={{px: 2.5, py: 2}}>
                    <Typography variant="body2" color="error">
                        Could not load owner licences.
                    </Typography>
                </Box>
            ) : null}
            {!isLoading && !error && licenses.length === 0 ? (
                <Box sx={{px: 2.5, py: 2}}>
                    <Typography variant="body2" color="text.secondary">
                        No licences on file for this owner.
                    </Typography>
                </Box>
            ) : null}
            {!isLoading && !error && licenses.length > 0 ? (
                <Table size="small" sx={{px: 0}}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Organisation</TableCell>
                            <TableCell>Licence number</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {licenses.map((lic) => (
                            <TableRow key={lic.id}>
                                <TableCell>{lic.organisation ?? '—'}</TableCell>
                                <TableCell>
                                    {lic.code != null && String(lic.code) !== ''
                                        ? String(lic.code)
                                        : '—'}
                                </TableCell>
                                <TableCell>
                                    {lic.expiry != null && String(lic.expiry) !== ''
                                        ? String(lic.expiry)
                                        : '—'}
                                </TableCell>
                                <TableCell>{lic.status ?? '—'}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        component={RouterLink}
                                        to={`/licenses/${lic.id}`}
                                        variant="outlined"
                                        size="small"
                                    >
                                        Edit licence
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}
        </Box>
    );
};

const UserDetails = () => {
    return (
        <Box sx={(theme) => ({...shellSx(theme), mb: 3})}>
            <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600}}
                >
                    Owner
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                    Linked owner account for this dog.
                </Typography>
            </Box>
            <Divider />
            <Box sx={{px: 2.5, py: 2.5}}>
                <Grid container spacing={2.5}>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            label="Name"
                            source="user_id"
                            reference="users"
                            link="show"
                        >
                            <FunctionField
                                render={(user) => formatUserDisplayName(user)}
                            />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            source="user_id"
                            reference="users"
                            label="Registered breeder"
                            link={false}
                        >
                            <BooleanField source="is_registered_breeder" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            source="user_id"
                            reference="users"
                            label="Suburb"
                            link={false}
                        >
                            <TextField source="location.suburb" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            source="user_id"
                            reference="users"
                            label="State"
                            link={false}
                        >
                            <TextField source="location.state" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            label="Male dogs"
                            source="user_id"
                            reference="users"
                            link={false}
                        >
                            <TextField source="count_male_dogs" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            label="Female dogs"
                            source="user_id"
                            reference="users"
                            link={false}
                        >
                            <TextField source="count_female_dogs" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            label="Phone"
                            source="user_id"
                            reference="users"
                            link={false}
                        >
                            <TextField source="phone_number" />
                        </ReferenceField>
                    </LabeledGridItem>

                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <ReferenceField
                            label="Email"
                            source="user_id"
                            reference="users"
                            link={false}
                        >
                            <TextField source="email" />
                        </ReferenceField>
                    </LabeledGridItem>
                </Grid>
            </Box>
        </Box>
    );
};

const BreedInputs = () => {
    const {data: breedsData} = useGetList<DogBreedData>('breeds', {});

    const choices = useMemo(
        () =>
            (breedsData || []).map((item) => ({id: item.id, name: item.label})),
        [breedsData]
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <AutocompleteInput
                    source="primary_breed_id"
                    validate={required()}
                    label="Primary breed"
                    choices={choices}
                />
            </Grid>
        </Grid>
    );
};

const ArtificialInseminationInput = () => {
    const sex = useWatch({name: 'sex'});

    if (sex === 'female') {
        return null;
    }

    return <BooleanInput source="artificial_insemination" />;
};

export const DogEdit = () => {
    return (
        <Grid container spacing={3} sx={{pb: 3}}>
            <Grid item xs={12} md={4}>
                <Edit title={<Title />} mutationMode="optimistic">
                    <AsideForm
                        sx={(theme) => ({
                            ...shellSx(theme),
                            '& .MuiCardContent-root': {
                                p: 0,
                            },
                        })}
                    >
                        <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{letterSpacing: '0.1em', fontWeight: 600}}
                            >
                                Overview
                            </Typography>
                            <TextInput
                                source="name"
                                label="Dog name"
                                validate={required()}
                                fullWidth
                                sx={{
                                    mt: 0.5,
                                    '& .MuiInputBase-input': {
                                        fontSize: '1.35rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em',
                                    },
                                }}
                            />
                        </Box>
                        <Divider />
                        <Box sx={{px: 2.5, py: 2}}>
                            <Grid
                                container
                                spacing={2}
                                alignItems="flex-end"
                                wrap="wrap"
                            >
                                <Grid item xs={12} sm>
                                    <SelectInput
                                        source="status"
                                        label="Status"
                                        choices={dogStatusChoices}
                                        validate={required()}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <SaveButton variant="contained" />
                                </Grid>
                            </Grid>
                        </Box>
                        <Divider />
                        <Box sx={{px: 2.5, py: 2}}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{letterSpacing: '0.1em', fontWeight: 600}}
                            >
                                Owner
                            </Typography>
                            <ReferenceField
                                source="user_id"
                                reference="users"
                                link="show"
                            >
                                <FunctionField
                                    render={(user) => (
                                        <Typography
                                            component="span"
                                            variant="body1"
                                            sx={{
                                                display: 'block',
                                                mt: 0.75,
                                                fontWeight: 600,
                                                letterSpacing: '-0.02em',
                                            }}
                                        >
                                            {formatUserDisplayName(user)}
                                        </Typography>
                                    )}
                                />
                            </ReferenceField>
                        </Box>
                        <Divider />
                        <Box
                            sx={(theme) => ({
                                px: 2.5,
                                py: 2,
                                bgcolor: alpha(theme.palette.common.black, 0.02),
                            })}
                        >
                            <ProfileImageField
                                source="profile_image.url"
                                label="Profile image"
                            />
                            <Box sx={{mt: 2}}>
                                <GalleryImages />
                            </Box>
                        </Box>
                    </AsideForm>
                </Edit>
            </Grid>
            <Grid item xs={12} md={8}>
                <Edit mutationMode="optimistic" title=" ">
                    <Box sx={(theme) => ({...shellSx(theme)})}>
                        <TabbedForm toolbar={<Toolbar />}>
                            <TabbedForm.Tab label="Registration">
                                <Box sx={{px: {xs: 2, sm: 2.5}, py: 2.5}}>
                                    <UserDetails />

                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12} sm={6}>
                                            <SelectInput
                                                source="registration_state"
                                                label="Registration state"
                                                choices={stateChoices}
                                                validate={required()}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextInput
                                                source="registration_number"
                                                label="Registration number"
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>

                                    <OwnerLicensesReadOnly />

                                    <Box sx={{mt: 2}}>
                                        <TextInput
                                            source="microchip_number"
                                            label="Microchip number"
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            </TabbedForm.Tab>
                            <TabbedForm.Tab label="Details">
                                <Box sx={{px: {xs: 2, sm: 2.5}, py: 2.5}}>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12} sm={6}>
                                            <SelectInput
                                                source="sex"
                                                choices={sexChoices}
                                                validate={required()}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <DateInput
                                                source="dob"
                                                label="Date of birth"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <NumberInput
                                                source="height"
                                                label="Height (cm)"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <NumberInput
                                                source="weight"
                                                label="Weight (kg)"
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{mt: 2}}>
                                        <BreedInputs />
                                    </Box>

                                    <Grid
                                        container
                                        spacing={2.5}
                                        sx={{mt: 1}}
                                    >
                                        <Grid item xs={12} sm={6}>
                                            <NumberInput
                                                source="litters_count"
                                                label="Litters count"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <DateInput
                                                source="health_check_date"
                                                label="Last vet visit"
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{mt: 2}}>
                                        <TextInput
                                            multiline
                                            source="introduction"
                                            label="Introduction"
                                            fullWidth
                                            minRows={3}
                                        />
                                    </Box>

                                    <Box sx={{mt: 2}}>
                                        <BooleanInput
                                            source="can_travel"
                                            label="Can travel"
                                        />
                                    </Box>
                                    <Box sx={{mt: 1}}>
                                        <ArtificialInseminationInput />
                                    </Box>
                                </Box>
                            </TabbedForm.Tab>
                        </TabbedForm>
                    </Box>
                </Edit>
            </Grid>
        </Grid>
    );
};
